import React, { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { useExplorerConfigurations, useDeleteExplorerConfiguration } from '../../../api/coreData'
import { useAppContext, useHideSideMenuEffect } from '../../../redux/slices/appContext'
import ExplorerConfigToolbar from './ExplorerConfigToolbar'
import PortfolioExplorerGrid from './PortfolioExplorerGrid'
import PreferencesDialog from './PreferencesDialog'
import { useExplorerConfig } from './hooks/useExplorerConfig'
import { useExperimentalFeature } from './hooks/useExperimentalFeature'
import { usePortfolioExplorerQuery } from './hooks/usePortfolioExplorerQuery'
import { usePortfolioExplorerPreferences } from './hooks/usePortfolioExplorerPreferences'
import { compileScopeFilter } from './lib/scopeFilterCompile'
import { getLastViewId, setLastViewId, getRecentViews, pushRecentView } from './lib/viewStorage'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: 'var(--opal-surface-default, #fff)'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #888)',
    fontSize: 14
  },
  error: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 14
  },
  errorDetail: {
    fontSize: 12,
    color: 'var(--color-neutral-500, #888)'
  }
}))

export default function PortfolioExplorer () {
  const classes = useStyles()
  const { userId } = useAppContext()
  useHideSideMenuEffect()

  const {
    config,
    columnDefinitions,
    setGroupings,
    setColumns,
    setQuickDate,
    setDateRange,
    setFetchAllLevels,
    setIncludeBenchmarks,
    setScopeFilters,
    loadConfig,
    serializeConfig,
    isDirty,
    markClean
  } = useExplorerConfig()

  const [asOfDate, setAsOfDate] = useState(() => dayjs().subtract(1, 'day').format('YYYY-MM-DD'))

  const { prefs, setPrefs } = usePortfolioExplorerPreferences(userId)
  // Scope filters (SMT-1608 Phase 1) are gated behind an experimental
  // opt-in. When the flag is off, the dialog hides Blade 3 and the query
  // hook strips `config.scopeFilters` from the effective config so no
  // `grouping.filter` is emitted on the Orion request -- stored filters
  // stay dormant in the saved view until the toggle is back on.
  const scopeFiltersEnabled = useExperimentalFeature('scopeFilters', prefs)
  // `hasActiveScopeFilters` is *only* true when (a) the experimental gate
  // is on and (b) at least one stored filter actually compiles to a
  // non-null value on the wire. Compiling here (rather than checking
  // `v != null` on the raw tree) avoids a false-positive asterisk for
  // predicates the user added but never gave a value to -- those compile
  // to null and don't emit a `grouping.filter`, so the household total
  // is genuinely unfiltered, and no caveat is needed. `compileScopeFilter`
  // is pure and cheap; running it once per grouping per render is
  // negligible.
  const hasActiveScopeFilters = scopeFiltersEnabled &&
    Object.values(config.scopeFilters || {}).some((v) => compileScopeFilter(v) != null)

  const {
    rows,
    totalRow,
    isFetching,
    error,
    refetch,
    expandRow,
    expandToLevelLazy,
    expandAllChildrenLazy,
    lazyExpandState,
    cancelLazyExpand,
    hasClient,
    isReady
  } = usePortfolioExplorerQuery(config, asOfDate, { scopeFiltersEnabled })

  const { data: savedConfigs, refetch: refetchConfigs } = useExplorerConfigurations()
  const deleteMutation = useDeleteExplorerConfiguration()

  const [activeConfigId, setActiveConfigId] = useState(null)
  const [activeViewName, setActiveViewName] = useState(null)
  const [recentViews, setRecentViews] = useState(() => getRecentViews(userId))
  const defaultLoadedRef = useRef(false)

  const [prefsDialogOpen, setPrefsDialogOpen] = useState(false)
  const openPrefs = useCallback(() => setPrefsDialogOpen(true), [])
  const closePrefs = useCallback(() => setPrefsDialogOpen(false), [])

  // Cmd+, (macOS) / Ctrl+, (other platforms) opens Preferences. Skip when focus
  // is in a text input — typing in a dialog field shouldn't pop another dialog.
  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target) return false
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (target.isContentEditable) return true
      return false
    }
    const onKeyDown = (e) => {
      // Cmd+Shift+. (mac) / Ctrl+Shift+. (other). The plain Cmd+, alternative
      // is captured by Chrome on macOS for chrome://settings, so the page
      // handler never fires there. Cmd+Shift+. avoids every browser-level
      // shortcut while still feeling like a "preferences" combo. Using
      // e.code ('Period') instead of e.key keeps this layout-independent --
      // Shift+. produces '>' on US layouts and the printable char varies on
      // others.
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return
      if (e.code !== 'Period') return
      if (isEditableTarget(e.target)) return
      e.preventDefault()
      setPrefsDialogOpen(prev => !prev)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (savedConfigs && !defaultLoadedRef.current) {
      defaultLoadedRef.current = true

      const lastId = getLastViewId(userId)
      const lastConfig = lastId ? savedConfigs.find(c => c.explorerConfigurationId === lastId) : null

      if (lastConfig) {
        loadConfig(lastConfig)
        setActiveConfigId(lastConfig.explorerConfigurationId)
        setActiveViewName(lastConfig.name)
        return
      }

      const defaultConfig = savedConfigs.find(c => c.isDefault)
      if (defaultConfig) {
        loadConfig(defaultConfig)
        setActiveConfigId(defaultConfig.explorerConfigurationId)
        setActiveViewName(defaultConfig.name)
        setLastViewId(userId, defaultConfig.explorerConfigurationId)
        pushRecentView(userId, defaultConfig.explorerConfigurationId, defaultConfig.name)
        setRecentViews(getRecentViews(userId))
      }
    }
  }, [savedConfigs, loadConfig, userId])

  const handleLoadConfig = useCallback((savedConfig) => {
    loadConfig(savedConfig)
    setActiveConfigId(savedConfig.explorerConfigurationId)
    setActiveViewName(savedConfig.name)
    setLastViewId(userId, savedConfig.explorerConfigurationId)
    pushRecentView(userId, savedConfig.explorerConfigurationId, savedConfig.name)
    setRecentViews(getRecentViews(userId))
  }, [loadConfig, userId])

  const handleSaveComplete = useCallback(({ explorerConfigurationId, name }) => {
    if (explorerConfigurationId != null) {
      setActiveConfigId(explorerConfigurationId)
      setLastViewId(userId, explorerConfigurationId)
      pushRecentView(userId, explorerConfigurationId, name)
      setRecentViews(getRecentViews(userId))
    }
    setActiveViewName(name)
    markClean()
  }, [markClean, userId])

  const handleDeleteConfig = useCallback((configId) => {
    deleteMutation.mutate(
      { explorerConfigurationId: configId },
      {
        onSuccess: () => {
          if (activeConfigId === configId) {
            setActiveConfigId(null)
            setActiveViewName(null)
            setLastViewId(userId, null)
          }
        }
      }
    )
  }, [deleteMutation, activeConfigId, userId])

  const canRun = hasClient && config.columns.length > 0 && isReady
  // `totalRow` is populated as soon as the response arrives, even when the
  // child rows list is empty (e.g. a scope filter excluded every account).
  // Treat the presence of either rows or a totalRow as "we got data" so the
  // user sees the grid + rollups instead of a "Run" placeholder when their
  // filter legitimately matched nothing.
  const hasResults = rows.length > 0 || totalRow != null || error

  return (
    <div className={classes.root}>
      <ExplorerConfigToolbar
        config={config}
        setGroupings={setGroupings}
        setColumns={setColumns}
        setQuickDate={setQuickDate}
        setDateRange={setDateRange}
        setFetchAllLevels={setFetchAllLevels}
        setIncludeBenchmarks={setIncludeBenchmarks}
        setScopeFilters={setScopeFilters}
        scopeFiltersEnabled={scopeFiltersEnabled}
        isFetching={isFetching}
        onRefresh={refetch}
        canRun={canRun}
        savedConfigs={savedConfigs || []}
        activeConfigId={activeConfigId}
        activeViewName={activeViewName}
        isDirty={isDirty}
        onLoadConfig={handleLoadConfig}
        onDeleteConfig={handleDeleteConfig}
        onRefreshConfigs={refetchConfigs}
        serializeConfig={serializeConfig}
        onSaveComplete={handleSaveComplete}
        recentViews={recentViews}
        asOfDate={asOfDate}
        setAsOfDate={setAsOfDate}
        onOpenPrefs={openPrefs}
      />

      <div className={classes.content}>
        {!hasClient && (
          <div className={classes.empty}>
            <p>Select a client to explore portfolio data.</p>
          </div>
        )}

        {hasClient && config.columns.length === 0 && (
          <div className={classes.empty}>
            <p>Select at least one column to display data.</p>
          </div>
        )}

        {canRun && isFetching && (
          <div className={classes.loading}>
            <CircularProgress size={32} />
          </div>
        )}

        {hasClient && error && !isFetching && (
          <div className={classes.error}>
            <p>Failed to load portfolio data.</p>
            <p className={classes.errorDetail}>{error.message || 'Unknown error'}</p>
          </div>
        )}

        {canRun && !isFetching && !hasResults && (
          <div className={classes.empty}>
            <p>Configure your view and click Run.</p>
          </div>
        )}

        {canRun && !isFetching && !error && (rows.length > 0 || totalRow != null) && (
          <PortfolioExplorerGrid
            rows={rows}
            columnDefinitions={columnDefinitions}
            totalRow={totalRow}
            onExpandRow={expandRow}
            groupings={config.groupings}
            expandToLevelLazy={expandToLevelLazy}
            expandAllChildrenLazy={expandAllChildrenLazy}
            lazyExpandState={lazyExpandState}
            cancelLazyExpand={cancelLazyExpand}
            viewName={activeViewName}
            asOfDate={asOfDate}
            prefs={prefs}
            hasActiveScopeFilters={hasActiveScopeFilters}
          />
        )}
      </div>

      <PreferencesDialog
        open={prefsDialogOpen}
        onClose={closePrefs}
        prefs={prefs}
        setPrefs={setPrefs}
      />
    </div>
  )
}
